/**
 * Message window component.
 * Displays messages with real-time updates, voice recording, and image upload.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Mic, Image as ImageIcon, X, Edit2, Trash2, Check, CheckCheck, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { ChatRoom, ChatMessage } from '../../services/chat';
import chatService from '../../services/chat';
import { useChatWebSocket } from '../../hooks/useChatWebSocket';
import { toast } from 'react-hot-toast';
import { formatRelativeTime } from '../../utils/dateUtils';
import VoiceRecorder from './VoiceRecorder';
import ImageUploader from './ImageUploader';
import { useAuthStore } from '../../stores/authStore';
import { useChatParticipants } from '../../hooks/useChatParticipants';
import AvatarDisplay from '../../components/ui/AvatarDisplay';
import { useConfirm } from '../../components/ConfirmDialog';
import { getImageUrl } from '../../utils/imageUtils';

interface MessageWindowProps {
  room: ChatRoom;
  onNewMessage?: (message: ChatMessage) => void;
  onRoomDeleted?: () => void;
}

/**
 * Parse read_by JSON string to check if message was read by anyone.
 */
function isMessageRead(readByJson: string | undefined): boolean {
  if (!readByJson) return false;
  try {
    const readBy = JSON.parse(readByJson);
    return Object.keys(readBy).length > 0;
  } catch {
    return false;
  }
}

const MessageWindow: React.FC<MessageWindowProps> = ({ room, onNewMessage, onRoomDeleted }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);
  const [typingUsers, setTypingUsers] = useState<Set<number>>(new Set());
  const [onlineUsers, setOnlineUsers] = useState<number[]>([]);
  const [showMembers, setShowMembers] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { user: currentUser } = useAuthStore();
  const { showConfirm } = useConfirm();
  const { getParticipantName } = useChatParticipants(room);

  // Check if user is admin or super_admin
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

  const upsertMessage = useCallback((incoming: ChatMessage) => {
    setMessages((prev) => {
      const existingIndex = prev.findIndex((msg) => msg.id === incoming.id);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = incoming;
        return updated;
      }
      return [...prev, incoming];
    });
  }, []);

  // Fonction pour jouer un son de notification
  const playNotificationSound = () => {
    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.3;
      audio.play().catch(() => {
        // Ignorer les erreurs de lecture audio (autoplay policy)
      });
    } catch (error) {
      // Ignorer les erreurs audio
    }
  };

  // Fetch online users
  const fetchOnlineUsers = useCallback(async () => {
    try {
      const response = await chatService.getRoomOnlineUsers(room.id);
      setOnlineUsers(response.online_users);
    } catch (error) {
      console.error('Failed to fetch online users:', error);
    }
  }, [room.id]);

  // WebSocket connection
  const { sendTyping } = useChatWebSocket({
    roomId: room.id,
    onNewMessage: (message) => {
      upsertMessage(message);
      scrollToBottom();

      // Jouer un son si ce n'est pas notre message
      const isClientAccount = currentUser?.role === 'client' || currentUser?.role === 'client_member';
      const isOwnMessage = isClientAccount
        ? message.sender_client_id === currentUser?.id
        : (message.sender_user_id === currentUser?.id || message.sender_employee_id === currentUser?.id);
      if (!isOwnMessage) {
        playNotificationSound();
      }

      // Notifier le parent pour mettre à jour le compteur
      if (onNewMessage) {
        onNewMessage(message);
      }
    },
    onTyping: (userId, isTyping) => {
      // Ne pas afficher l'indicateur pour notre propre saisie
      if (userId === currentUser?.id) return;

      setTypingUsers((prev) => {
        const newSet = new Set(prev);
        if (isTyping) {
          newSet.add(userId);
        } else {
          newSet.delete(userId);
        }
        return newSet;
      });
    },
    onReadReceipt: (messageId, userId) => {
      // Mettre à jour le message avec le read_by
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === messageId) {
            // Parser read_by JSON
            let readBy: Record<string, string> = {};
            if (msg.read_by) {
              try {
                readBy = JSON.parse(msg.read_by);
              } catch {
                readBy = {};
              }
            }
            // Ajouter le lecteur
            readBy[String(userId)] = new Date().toISOString();
            return {
              ...msg,
              read_by: JSON.stringify(readBy),
            };
          }
          return msg;
        })
      );
    },
  });

  // Charger les messages
  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await chatService.listMessages(room.id);
      setMessages(data);
      scrollToBottom();

      // Marquer tous les messages comme lus
      if (data.length > 0) {
        try {
          await chatService.markAsRead(room.id);
        } catch (error) {
          console.error('Error marking as read:', error);
        }
      }
    } catch (error: any) {
      console.error('Error loading messages:', error);
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
    fetchOnlineUsers();
  }, [room.id, fetchOnlineUsers]);

  // Fetch online users periodically
  useEffect(() => {
    const interval = setInterval(fetchOnlineUsers, 60000); // Update every 60 seconds
    return () => clearInterval(interval);
  }, [fetchOnlineUsers]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Gérer la saisie
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);

    // Envoyer indicateur "en train d'écrire"
    sendTyping(true);

    // Arrêter l'indicateur après 3 secondes d'inactivité
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      sendTyping(false);
    }, 3000);
  };

  // Envoyer un message texte
  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!inputText.trim() || sending) return;

    try {
      setSending(true);
      console.log('Sending typing indicator: false (message sent)');
      sendTyping(false);

      if (editingMessage) {
        // Modifier le message existant
        const updated = await chatService.editMessage(room.id, editingMessage.id, inputText);
        setMessages((prev) =>
          prev.map((msg) => (msg.id === updated.id ? updated : msg))
        );
        setEditingMessage(null);
      } else {
        // Envoyer un nouveau message avec attribution du rôle
        const senderRole = currentUser?.role === 'apporteur' ? 'AA' : (currentUser?.role === 'admin' || currentUser?.role === 'super_admin') ? 'Support' : undefined;

        await chatService.sendMessageWithAttribution(room.id, {
          content: inputText,
          type: 'TEXT',
          sender_role: senderRole,
        });
        // Le message sera ajouté automatiquement via WebSocket - pas d'ajout manuel
      }

      setInputText('');
      scrollToBottom();
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  // Gérer l'enregistrement vocal
  const handleVoiceRecorded = async (audioBlob: Blob) => {
    try {
      setSending(true);

      // Créer un message vide pour l'audio avec attribution du rôle
      const senderRole = currentUser?.role === 'apporteur' ? 'AA' : (currentUser?.role === 'admin' || currentUser?.role === 'super_admin') ? 'Support' : undefined;

      const message = await chatService.sendMessageWithAttribution(room.id, {
        type: 'VOICE',
        sender_role: senderRole,
      });

      // Upload l'audio
      const audioFile = new File([audioBlob], 'voice-message.webm', {
        type: 'audio/webm',
      });
      await chatService.uploadAttachment(room.id, message.id, audioFile);

      // Le message vocal sera mis à jour automatiquement via WebSocket
      toast.success('Message vocal envoyé');
      setShowVoiceRecorder(false);
    } catch (error: any) {
      console.error('Error sending voice message:', error);
      toast.error('Erreur lors de l\'envoi du message vocal');
    } finally {
      setSending(false);
    }
  };

  // Gérer l'upload d'image
  const handleImageUploaded = async (imageFile: File) => {
    try {
      setSending(true);

      // Créer un message vide pour l'image avec attribution du rôle
      const senderRole = currentUser?.role === 'apporteur' ? 'AA' : (currentUser?.role === 'admin' || currentUser?.role === 'super_admin') ? 'Support' : undefined;

      const message = await chatService.sendMessageWithAttribution(room.id, {
        type: 'IMAGE',
        sender_role: senderRole,
      });

      // Upload l'image
      await chatService.uploadAttachment(room.id, message.id, imageFile);

      // Le message image sera mis à jour automatiquement via WebSocket
      toast.success('Image envoyée');
      setShowImageUploader(false);
    } catch (error: any) {
      console.error('Error sending image:', error);
      toast.error('Erreur lors de l\'envoi de l\'image');
    } finally {
      setSending(false);
    }
  };

  // Supprimer un message
  const handleDeleteMessage = async (messageId: number) => {
    const confirmed = await showConfirm({
      title: 'Supprimer le message',
      message: 'Êtes-vous sûr de vouloir supprimer ce message ? Cette action est irréversible.',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      severity: 'error'
    });

    if (!confirmed) return;

    try {
      await chatService.deleteMessage(room.id, messageId);
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
      toast.success('Message supprimé');
    } catch (error: any) {
      console.error('Error deleting message:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Supprimer la conversation (admin/super_admin only)
  const handleDeleteRoom = async () => {
    const confirmed = await showConfirm({
      title: 'Supprimer la conversation',
      message: 'Êtes-vous sûr de vouloir supprimer cette conversation ? Cette action est irréversible.',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      severity: 'error'
    });

    if (!confirmed) return;

    try {
      await chatService.deleteRoom(room.id);
      toast.success('Conversation supprimée');
      if (onRoomDeleted) {
        onRoomDeleted();
      }
    } catch (error: any) {
      console.error('Error deleting room:', error);
      toast.error('Erreur lors de la suppression de la conversation');
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-white dark:bg-gradient-to-b dark:from-gray-950 dark:to-gray-900 h-full">
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-gray-900/80 backdrop-blur">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 tracking-tight truncate">
              {/* Afficher le nom du destinataire (priorité aux champs backend) */}
              {room.type === 'DIRECT'
                ? (room.peer_display_name || (() => {
                  if (!room.participants || room.participants.length === 0) return 'Conversation directe';
                  const myId = currentUser?.id;
                  const myRole = currentUser?.role;
                  const meMatcher = (p: any) => {
                    if (!myId) return false;
                    if (myRole === 'client') return p.client_id === myId;
                    if (myRole === 'employee') return p.employee_id === myId;
                    return p.user_id === myId;
                  };
                  const other = room.participants!.find((p) => !meMatcher(p));
                  if (other) return getParticipantName(other.user_id, other.employee_id, (other as any).client_id);
                  return 'Conversation directe';
                })())
                : (room.peer_display_name || room.name || 'Conversation de groupe')}
            </h2>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
            <div className="flex items-center space-x-2">
              {room.type === 'DIRECT' ? (
                // For direct chats, show the other participant's online status
                (() => {
                  const otherParticipant = room.participants?.find(p =>
                    (p.user_id !== currentUser?.id) ||
                    (p.employee_id !== currentUser?.id) ||
                    (p.client_id !== currentUser?.id)
                  );
                  const otherUserId = otherParticipant?.user_id || otherParticipant?.employee_id || otherParticipant?.client_id;
                  const isOtherUserOnline = otherUserId ? onlineUsers.includes(otherUserId) : false;

                  return (
                    <span className={`flex items-center space-x-1 text-xs sm:text-sm ${isOtherUserOnline ? 'text-green-500' : 'text-gray-400 dark:text-gray-500'
                      }`}>
                      <span className={`w-2 h-2 rounded-full ${isOtherUserOnline ? 'bg-green-500' : 'bg-gray-400'
                        }`}></span>
                      <span className="hidden sm:inline">{isOtherUserOnline ? 'En ligne' : 'Hors ligne'}</span>
                    </span>
                  );
                })()
              ) : (
                // For group chats, show online count and members toggle
                <button
                  onClick={() => setShowMembers(!showMembers)}
                  className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                >
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">{room.participants?.length || 0} membres</span>
                  <span className="sm:hidden">{room.participants?.length || 0}</span>
                  {showMembers ? <ChevronUp className="h-3 w-3 sm:h-4 sm:w-4" /> : <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4" />}
                </button>
              )}
            </div>

            {/* Delete room button for admins */}
            {isAdmin && (
              <button
                onClick={handleDeleteRoom}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                title="Supprimer la conversation"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Members panel for GROUP rooms */}
        {room.type === 'GROUP' && showMembers && room.participants && room.participants.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-2">
              {room.participants.map((participant: any, idx) => {
                // Use display_name from backend if available, otherwise fallback to hook
                const name = participant.display_name || getParticipantName(participant.user_id, participant.employee_id, participant.client_id);
                const participantId = participant.user_id || participant.employee_id || participant.client_id;
                const isOnline = participantId ? onlineUsers.includes(participantId) : false;

                return (
                  <div
                    key={idx}
                    className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full"
                  >
                    <div className="relative">
                      <AvatarDisplay
                        avatarUrl={undefined}
                        name={(() => {
                          // For apporteur view: show "Support" for admin messages to match chat
                          if (currentUser?.role === 'apporteur' && participant.user_id && !participant.client_id && participant.user_id !== currentUser?.id) {
                            return 'Support';
                          }
                          return name;
                        })()}
                        size="w-5 h-5 sm:w-6 sm:h-6"
                        userRole={(() => {
                          // Determine role for consistent colors with chat messages
                          if (participant.client_id) {
                            return 'client'; // Green for clients
                          }

                          // For apporteur view - same logic as chat messages
                          if (currentUser?.role === 'apporteur') {
                            if (participant.user_id === currentUser?.id) {
                              return 'apporteur'; // Orange for self (apporteur)
                            } else if (participant.user_id) {
                              return 'admin'; // Blue for admins/support when viewed by apporteur
                            }
                          }

                          // For admin view - same logic as chat messages  
                          if (currentUser?.role === 'admin' || currentUser?.role === 'super_admin') {
                            if (participant.user_id === currentUser?.id) {
                              return 'admin'; // Blue for self (admin)
                            } else if (participant.user_id) {
                              return 'apporteur'; // Orange for apporteurs when viewed by admin
                            }
                          }

                          // Default fallback
                          if (participant.user_id) {
                            return 'admin'; // Default to admin (blue)
                          }

                          return undefined;
                        })()}
                      />
                      {isOnline && (
                        <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></span>
                      )}
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Messages - Facebook style background */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-1 bg-white dark:bg-gray-900 min-h-0">
        {messages.map((message, _index) => {
          const isClientAccount = currentUser?.role === 'client' || currentUser?.role === 'client_member';
          const isOwn = isClientAccount
            ? message.sender_client_id === currentUser?.id
            : (
              message.sender_user_id === currentUser?.id ||
              message.sender_employee_id === currentUser?.id
            );

          // Debug logging for message ownership
          console.log(`[MessageWindow] Message ${message.id} ownership check:`, {
            messageId: message.id,
            senderUserId: message.sender_user_id,
            senderEmployeeId: message.sender_employee_id,
            senderBuyerId: (message as any).sender_buyer_id,
            currentUserId: currentUser?.id,
            currentUserType: typeof currentUser?.id,
            isOwn,
            content: message.content,
            senderDisplayName: message.sender_display_name,
            justifyClass: isOwn ? 'justify-end' : 'justify-start'
          });
          const showAvatar = true; // Force avatar display for debugging

          // Déterminer le nom de l'expéditeur sans rôle
          const getSenderName = () => {
            if (isOwn) return 'Vous';

            // For clients, always show "Support" regardless of who sent the message
            if (currentUser?.role === 'client') {
              return 'Support';
            }

            // For internal users, show clean name without role
            let baseName = message.sender_display_name || getParticipantName(
              message.sender_user_id,
              message.sender_employee_id,
              message.sender_client_id
            );

            // Clean the name by removing role suffixes
            baseName = baseName
              ?.replace(/\s*\(.*?\)$/, '') // Remove anything in parentheses at the end
              ?.replace(/\s*-\s*(Super Admin|Admin|Apporteur|AA)$/i, '') // Remove role suffixes
              ?.replace(/^(Super Admin|Admin|Apporteur|AA)\s*-?\s*/i, '') // Remove role prefixes
              ?.trim();

            // If viewing from apporteur and it's an admin message, show "Support"
            if (currentUser?.role === 'apporteur' && message.sender_user_id && !message.sender_client_id) {
              return 'Support';
            }

            return baseName || 'Support';
          };

          // Déterminer l'attribution pour affichage séparé
          const getAttributionText = () => {
            if (isOwn || currentUser?.role === 'client') return null;

            console.log('Message attribution debug:', {
              messageId: message.id,
              senderRole: message.sender_role,
              senderUserId: message.sender_user_id,
              currentUserRole: currentUser?.role,
              roomName: room.name,
              roomType: room.type,
              senderClientId: message.sender_client_id,
              senderDisplayName: message.sender_display_name
            });

            // Fallback: Si pas de sender_role, détecter selon l'utilisateur et le contexte
            let senderRole = message.sender_role;

            // Si pas de sender_role et c'est un message d'un autre utilisateur
            if (!senderRole && message.sender_user_id && message.sender_user_id !== currentUser?.id) {
              // Dans un contexte de support, détecter automatiquement
              if (room.name?.toLowerCase().includes('support') || room.type === 'GROUP' || room.name?.toLowerCase().includes('client')) {
                if (currentUser?.role === 'admin' || currentUser?.role === 'super_admin') {
                  // Si pas un client, c'est probablement un AA
                  senderRole = message.sender_client_id ? undefined : 'AA';
                } else if (currentUser?.role === 'apporteur') {
                  // Si c'est un apporteur qui regarde, les autres sont Support
                  senderRole = message.sender_client_id ? undefined : 'Support';
                }
              }
            }

            // DEBUG: Force affichage temporaire pour test
            if (!senderRole && !isOwn && message.sender_user_id && message.sender_user_id !== currentUser?.id && !message.sender_client_id) {
              senderRole = 'AA'; // Force pour test
            }

            if (senderRole === 'AA') {
              const senderName = message.sender_display_name || getParticipantName(
                message.sender_user_id,
                message.sender_employee_id,
                message.sender_client_id
              );
              return `Répondu par apporteur : ${senderName}`;
            }

            if (senderRole === 'Support') {
              return 'Répondu par Support';
            }

            return null;
          };

          // In GROUP rooms, always show sender name for admins/super_admins
          const shouldShowSenderName = room.type === 'GROUP' && showAvatar;

          return (
            <div key={message.id}>
              {/* For clients: show only time, no sender names for transparency */}
              {currentUser?.role === 'client' && !isOwn && (
                <div className="flex items-center space-x-2 mb-1 ml-10 sm:ml-12">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatRelativeTime(message.created_at)}
                  </span>
                </div>
              )}

              {/* For internal users: show sender names for received messages */}
              {currentUser?.role !== 'client' && shouldShowSenderName && !isOwn && (
                <div className="flex items-center space-x-2 mb-1 ml-10 sm:ml-12">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{getSenderName()}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatRelativeTime(message.created_at)}
                  </span>
                </div>
              )}

              {/* For internal users in DIRECT rooms */}
              {currentUser?.role !== 'client' && !shouldShowSenderName && showAvatar && !isOwn && (
                <div className="flex items-center space-x-2 mb-1 ml-10 sm:ml-12">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{getSenderName()}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatRelativeTime(message.created_at)}
                  </span>
                </div>
              )}

              <div className={`flex items-end space-x-2 ${isOwn ? 'justify-end' : 'justify-start'} mb-1
                }`}>
                {/* Avatar pour messages reçus */}
                {!isOwn && (
                  <div className={`flex-shrink-0 ${showAvatar ? 'opacity-100' : 'opacity-0'}`}>
                    {currentUser?.role === 'client' ? (
                      // Client view: uniform 'S' avatar for all support messages
                      <AvatarDisplay
                        avatarUrl={undefined}
                        name="Support"
                        size="w-7 h-7 sm:w-8 sm:h-8"
                        userRole="admin" // Blue color for Support
                      />
                    ) : (
                      // Internal view: role-based avatars
                      <AvatarDisplay
                        avatarUrl={undefined}
                        name={(() => {
                          // For apporteur viewing admin messages, always show "Support"
                          if (currentUser?.role === 'apporteur' && message.sender_user_id && !message.sender_client_id) {
                            return 'Support';
                          }
                          return message.sender_display_name || getSenderName();
                        })()}
                        size="w-7 h-7 sm:w-8 sm:h-8"
                        userRole={(() => {
                          // Debug: déterminer le rôle et l'afficher pour les utilisateurs internes
                          let role = undefined;
                          if (message.sender_role === 'AA') {
                            role = 'apporteur';
                          } else if (message.sender_role === 'Support') {
                            role = 'admin';
                          } else if (message.sender_user_id && !message.sender_client_id) {
                            // Fallback: pour apporteur regardant admin, c'est Support (admin role)
                            if (currentUser?.role === 'apporteur') {
                              role = 'admin'; // Blue for Support when viewed by apporteur
                            } else {
                              role = 'apporteur'; // Orange for apporteur when viewed by admin
                            }
                          } else if (message.sender_client_id) {
                            role = 'client';
                          }

                          console.log('Avatar role debug:', {
                            messageId: message.id,
                            senderRole: message.sender_role,
                            detectedRole: role,
                            senderUserId: message.sender_user_id,
                            senderClientId: message.sender_client_id,
                            senderName: message.sender_display_name || getSenderName()
                          });

                          return role;
                        })()}
                      />
                    )}
                  </div>
                )}

                {/* Message bubble - Facebook style */}
                <div className={`max-w-xs sm:max-w-sm px-3 sm:px-4 py-2 sm:py-2.5 ${isOwn
                  ? 'bg-blue-600 text-white rounded-2xl rounded-br-md shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-2xl rounded-bl-md shadow-sm'
                  }`}>
                  {/* Message content */}
                  {message.type === 'TEXT' && (
                    <p className="text-sm leading-normal">{message.content}</p>
                  )}

                  {message.type === 'IMAGE' && message.attachments && message.attachments.length > 0 && (
                    <img
                      src={getImageUrl(message.attachments[0].file_path)}
                      alt="Image"
                      className="max-w-full rounded-lg"
                    />
                  )}

                  {message.type === 'VOICE' && message.attachments && message.attachments.length > 0 && (
                    <audio controls className="max-w-full">
                      <source src={getImageUrl(message.attachments[0].file_path)} type="audio/webm" />
                    </audio>
                  )}

                  {/* Métadonnées */}
                  <div className={`flex items-center justify-between mt-1.5 text-[10px] ${isOwn ? 'text-blue-200' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                    <div className="flex items-center space-x-2">
                      {isOwn && (
                        <span>
                          {formatRelativeTime(message.created_at)}
                        </span>
                      )}
                      {message.is_edited && <span>(modifié)</span>}
                    </div>

                    <div className="flex items-center space-x-1">
                      {/* Accusés de lecture pour messages envoyés */}
                      {isOwn && (
                        <div className="flex items-center space-x-1">
                          {/* Statut d'envoi */}
                          <div className="flex items-center space-x-1 mr-2" title={isMessageRead(message.read_by) ? "Lu" : "Envoyé"}>
                            {isMessageRead(message.read_by) ? (
                              <>
                                <CheckCheck className="h-3 w-3 text-blue-200" />
                                <span className="text-xs">Lu</span>
                              </>
                            ) : (
                              <Check className="h-3 w-3 text-blue-300" />
                            )}
                          </div>

                          {/* Actions */}
                          <button
                            onClick={() => {
                              setEditingMessage(message);
                              setInputText(message.content || '');
                              inputRef.current?.focus();
                            }}
                            className="hover:text-white opacity-70 hover:opacity-100 transition-opacity"
                            title="Modifier"
                          >
                            <Edit2 className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => handleDeleteMessage(message.id)}
                            className="hover:text-white opacity-70 hover:opacity-100 transition-opacity"
                            title="Supprimer"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Espace pour l'avatar des messages envoyés (pour l'alignement) */}
                {isOwn && <div className="w-7 sm:w-8"></div>}
              </div>

              {/* Attribution display for internal users - below message */}
              {!isOwn && getAttributionText() && (
                <div className={`flex items-center mt-1 ${isOwn ? 'justify-end mr-10 sm:mr-12' : 'ml-10 sm:ml-12'}`}>
                  <span className="text-xs text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded">
                    {getAttributionText()}
                  </span>
                </div>
              )}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Indicateur de saisie */}
      {typingUsers.size > 0 && (
        <div className="flex-shrink-0 px-4 sm:px-6 py-2 bg-gray-50 dark:bg-gray-900/40 border-t border-gray-100 dark:border-gray-800">
          <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
            <span>En train d'écrire...</span>
          </div>
        </div>
      )}

      {/* Facebook-style input area */}
      <div className="flex-shrink-0 px-3 py-3 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
        {editingMessage && (
          <div className="mb-3 flex items-center justify-between px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <span className="text-sm text-blue-700 dark:text-blue-200">
              Modification du message...
            </span>
            <button
              onClick={() => {
                setEditingMessage(null);
                setInputText('');
              }}
              className="text-blue-600 dark:text-blue-300 hover:text-blue-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <form onSubmit={handleSendMessage} className="flex items-end space-x-2">
          {/* Media buttons */}
          <div className="flex space-x-1">
            <button
              type="button"
              onClick={() => setShowImageUploader(true)}
              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition-colors flex-shrink-0"
              title="Envoyer une image"
            >
              <ImageIcon className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={() => setShowVoiceRecorder(true)}
              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition-colors flex-shrink-0"
              title="Enregistrer un message vocal"
            >
              <Mic className="h-5 w-5" />
            </button>
          </div>

          {/* Input container - Facebook style */}
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={handleInputChange}
              placeholder="Aa"
              className="w-full px-4 py-2.5 rounded-full border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white dark:focus:bg-gray-700 transition-all text-sm"
            />

            {/* Send button - integrated Facebook style */}
            {inputText.trim() && (
              <button
                type="submit"
                disabled={sending}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <Send className="h-4 w-4" />
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Modals */}
      {showVoiceRecorder && (
        <VoiceRecorder
          onRecorded={handleVoiceRecorded}
          onClose={() => setShowVoiceRecorder(false)}
        />
      )}

      {showImageUploader && (
        <ImageUploader
          onUploaded={handleImageUploaded}
          onClose={() => setShowImageUploader(false)}
        />
      )}
    </div>
  );
};

export default MessageWindow;
